<!--
  OrganizationEnrichModal.vue

  Shown before an organization record is created in Auger, so the user can
  review/edit data enriched from Harmonic.ai before committing. Opened
  either directly via useOrganizationEnrich, or from QuickCreateModal when
  the user clicks "Create new organization" from the person flow.

  Inputs:
    - name   : initial org name (always required)
    - domain : optional email/website domain used to query Harmonic

  On save, creates an `organization` record (+ website/LinkedIn contacts)
  and emits `created` with the new record.
-->
<template>
  <ModalContainer :show="show" @close="cancel">
    <div class="org-enrich-modal" style="width: 480px">
      <div class="modal-header">
        <div class="modal-header-left">
          <img
            v-if="form.logo && !brokenLogo"
            class="org-logo"
            :src="form.logo"
            :alt="form.name"
            @error="brokenLogo = true"
          />
          <div v-else class="org-logo org-logo-initials">
            {{ initials(form.name) || "?" }}
          </div>
          <h2 class="text-4">New Organization</h2>
        </div>
        <div v-if="enriching" class="text-muted text-7">
          <font-awesome-icon icon="spinner" spin class="mr-1" />
          Enriching via Harmonic…
        </div>
        <div v-else-if="enriched" class="text-7 enriched-badge">
          <font-awesome-icon icon="check" class="mr-1" />
          Enriched
        </div>
      </div>

      <div class="field">
        <label class="label is-small">Name <span class="text-danger">*</span></label>
        <input
          ref="nameInput"
          v-model="form.name"
          class="input"
          type="text"
          placeholder="Organization name"
          @keydown.enter.prevent="submit"
        />
      </div>

      <div class="field">
        <label class="label is-small">Website</label>
        <input
          v-model="form.website"
          class="input"
          type="url"
          placeholder="https://example.com"
          @keydown.enter.prevent="submit"
        />
      </div>

      <div class="field">
        <label class="label is-small">LinkedIn</label>
        <input
          v-model="form.linkedin"
          class="input"
          type="url"
          placeholder="https://linkedin.com/company/..."
          @keydown.enter.prevent="submit"
        />
      </div>

      <div class="field">
        <label class="label is-small">Description</label>
        <textarea
          v-model="form.description"
          class="textarea"
          rows="3"
          placeholder="Short description for context"
        />
      </div>

      <!-- Read-only metadata from Harmonic, shown if present -->
      <div v-if="meta.length" class="enriched-meta">
        <div v-for="m in meta" :key="m.label" class="meta-row">
          <span class="text-muted text-7">{{ m.label }}:</span>
          <span class="text-7">{{ m.value }}</span>
        </div>
      </div>

      <div v-if="enrichError" class="enrich-warning text-7 text-muted">
        Couldn't reach Harmonic — you can still create the organization manually.
      </div>
      <div
        v-else-if="!enriching && !enriched && (seedDomain || seedName)"
        class="enrich-warning text-7 text-muted"
      >
        No Harmonic match for <b>{{ seedDomain || seedName }}</b
        >. You can still fill this in manually.
      </div>

      <!-- Dup-match gate: shown when the typed name normalizes to existing auger
           orgs, OR when the dedup search failed (so a backend blip can't
           silently pass the gate). Blocks Create until the user picks an
           existing record or explicitly acknowledges. -->
      <div v-if="dupMatches.length || dupCheckError" class="dup-warning">
        <div class="dup-warning-header text-7">
          <font-awesome-icon icon="triangle-exclamation" class="mr-1" />
          <template v-if="dupMatches.length">
            Possible duplicate{{ dupMatches.length > 1 ? "s" : "" }} already in Auger:
          </template>
          <template v-else> Couldn't check Auger for existing organizations. </template>
        </div>
        <div v-for="m in dupMatches" :key="m.id" class="dup-match-row">
          <span class="dup-match-name">{{ m.name }}</span>
          <button class="btn btn-sm" type="button" :disabled="submitting" @click="useExisting(m)">
            Use this one
          </button>
        </div>
        <label class="dup-ack text-7">
          <input v-model="dupCheckAcked" type="checkbox" />
          {{
            dupMatches.length
              ? "Create anyway — these are different organizations"
              : "Create anyway without checking"
          }}
        </label>
      </div>

      <div class="field is-grouped justify-end mt-5">
        <div class="control">
          <button class="btn btn-sm" @click="cancel">Cancel</button>
        </div>
        <div class="control">
          <button
            class="btn btn-sm btn-primary"
            :disabled="!canSubmit || submitting"
            @click="submit"
          >
            {{ submitting ? "Creating..." : "Create Organization" }}
          </button>
        </div>
      </div>
    </div>
  </ModalContainer>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { toast } from "vue-sonner"
import { debounce } from "lodash-es"
import type { ContactTarget, CreateRecordInput } from "@auger/core"
import { normalizeOrgName } from "@auger/ui/utils/string"
import { useAssets } from "@/composables/useAssets"
import { useAvatarWrite } from "@/composables/useAvatarWrite"
import { enrichCompany, enrichCompanyByName, type HarmonicCompany } from "@/composables/useHarmonic"
import { type OrganizationRef, useOrganizationEnrich } from "@/composables/useOrganizationEnrich"
import { client } from "@/init/apollo"
import { getAuger } from "@/init/auger"
import { errorMessage } from "@/utils/errors"

// Flatten Harmonic's raw company payload into the keys this modal reads.
function harmonicCompanyView(raw: HarmonicCompany | undefined) {
  if (!raw) return null
  const website =
    (typeof raw.website === "string" ? raw.website : raw.website?.url) ||
    (raw.website_domain_aliases?.[0] ? `https://${raw.website_domain_aliases[0]}` : null)
  return {
    name: raw.name || raw.legal_name || null,
    description: raw.description || raw.short_description || raw.external_description || null,
    website,
    linkedin: raw.socials?.LINKEDIN?.url || null,
    twitter: raw.socials?.TWITTER?.url || null,
    facebook: raw.socials?.FACEBOOK?.url || null,
    logo: raw.logo_url || null,
    headcount: raw.headcount ?? null,
    stage: raw.stage || null,
    location: raw.location?.address_formatted || raw.location?.city || null,
  }
}
import ModalContainer from "@auger/ui/components/ModalContainer.vue"
import { getType } from "@/stores/types"

const { uploadUrl } = useAssets({ client, toast: null })
const { attachAndUpdate } = useAvatarWrite({ client, toast: null })

const orgEnrich = useOrganizationEnrich()
const { show, options } = orgEnrich
const seedName = computed(() => options.value.name || "")
const seedDomain = computed(() => options.value.domain || "")

const nameInput = ref<HTMLInputElement | null>(null)
const submitting = ref(false)
const enriching = ref(false)
const enriched = ref(false)
const enrichError = ref(false)
const brokenLogo = ref(false)

const form = ref({
  name: "",
  website: "",
  linkedin: "",
  description: "",
  logo: "",
})

// Existing auger orgs whose normalized name equals the typed name. Filled by
// runDupSearch (fired by a watcher on form.name, search debounced); the open
// watcher resets it. `dupCheckError` is set when the search itself fails, so a
// failed check holds the gate closed instead of silently passing. The user
// must pick a match, or check `dupCheckAcked`, before Create is enabled.
const dupMatches = ref<OrganizationRef[]>([])
const dupCheckError = ref(false)
const dupCheckAcked = ref(false)

const canSubmit = computed(
  () =>
    !!form.value.name.trim() &&
    ((dupMatches.value.length === 0 && !dupCheckError.value) || dupCheckAcked.value)
)

// Search auger for orgs whose normalized name equals the typed name. A sequence
// number discards out-of-order responses so only the latest query's results
// apply (this is stale-result cancellation, not rate-limiting — the watcher
// debounces the call itself).
let dupSearchSeq = 0
async function runDupSearch(rawName: string) {
  const target = normalizeOrgName(rawName)
  if (target.length < 2) {
    dupMatches.value = []
    dupCheckError.value = false
    return
  }
  const seq = ++dupSearchSeq
  try {
    // Substring ILIKE on %target% over-fetches (matches "Tonic" and "Tonic AI"
    // when target="tonic"); we keep only exact normalized matches client-side.
    // The query orders by name asc, so a generous limit keeps the exact match
    // from being windowed out by alphabetically-earlier near-matches.
    const auger = getAuger(client)
    const results = await auger.records.search(`%${target}%`, { type: "organization", limit: 50 })
    if (seq !== dupSearchSeq) return
    dupMatches.value = results.filter((r) => normalizeOrgName(r.name) === target)
    dupCheckError.value = false
  } catch (err) {
    if (seq !== dupSearchSeq) return
    // Dedup is a safety gate — a failed check must not silently pass. Flag the
    // error so canSubmit stays blocked until the user explicitly acknowledges.
    console.error("Dup-match search failed:", err)
    dupMatches.value = []
    dupCheckError.value = true
  }
}

const debouncedDupSearch = debounce(runDupSearch, 250)

watch(
  () => form.value.name,
  (name) => {
    // Editing the name re-locks the gate immediately; the network search itself
    // is debounced so we don't query Hasura on every keystroke.
    dupCheckAcked.value = false
    debouncedDupSearch(name)
  }
)

// Treat "use existing" the same as a successful create — emit the existing
// record via onCreated and close the modal.
function useExisting(record: OrganizationRef) {
  orgEnrich.created(record)
}

// Human-readable metadata shown read-only under the form. These are persisted
// onto record.data at create time (see submit()) and shown here so the user
// can verify we matched the right company before saving.
const meta = computed(() => {
  const out = []
  if (extra.value.headcount) out.push({ label: "Headcount", value: extra.value.headcount })
  if (extra.value.stage) out.push({ label: "Stage", value: extra.value.stage })
  if (extra.value.location) out.push({ label: "Location", value: extra.value.location })
  return out
})

// Enriched fields not exposed as form inputs but persisted on create.
// twitter/facebook are saved as link contacts; the rest go on record.data.
interface EnrichedExtra {
  headcount: number | null
  stage: string | null
  location: string | null
  twitter: string | null
  facebook: string | null
}
const extra = ref<EnrichedExtra>({
  headcount: null,
  stage: null,
  location: null,
  twitter: null,
  facebook: null,
})

function initials(name: string) {
  if (!name) return ""
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("")
}

// Reset state and kick off enrichment each time the modal opens.
watch(
  show,
  async (isOpen) => {
    if (!isOpen) return

    form.value = {
      name: seedName.value,
      website: seedDomain.value ? `https://${seedDomain.value}` : "",
      linkedin: "",
      description: "",
      logo: "",
    }
    extra.value = {
      headcount: null,
      stage: null,
      location: null,
      twitter: null,
      facebook: null,
    }
    enriched.value = false
    enrichError.value = false
    brokenLogo.value = false
    submitting.value = false
    dupMatches.value = []
    dupCheckError.value = false
    dupCheckAcked.value = false

    nextTick(() => nameInput.value?.focus())

    // Try domain first (more precise); fall back to name-based typeahead.
    if (!seedDomain.value && !seedName.value) return
    enriching.value = true
    try {
      const result = seedDomain.value
        ? await enrichCompany({ website_domain: seedDomain.value })
        : await enrichCompanyByName(seedName.value)
      if (!show.value) return // modal was closed mid-request
      if (result.found) {
        const d = harmonicCompanyView(result.data)
        if (!d) return
        // Only prefill fields the user hasn't touched (still empty from reset).
        if (d.name && !form.value.name.trim()) form.value.name = d.name
        if (d.website && !form.value.website.trim()) form.value.website = d.website
        if (d.linkedin && !form.value.linkedin.trim()) form.value.linkedin = d.linkedin
        if (d.description && !form.value.description.trim()) form.value.description = d.description
        if (d.logo) form.value.logo = d.logo
        extra.value = {
          headcount: d.headcount,
          stage: d.stage,
          location: d.location,
          twitter: d.twitter,
          facebook: d.facebook,
        }
        enriched.value = true
      }
    } catch (err) {
      console.error("Harmonic org enrichment failed:", err)
      enrichError.value = true
    } finally {
      enriching.value = false
    }
  },
  { immediate: true }
)

function cancel() {
  orgEnrich.close()
}

async function tryCreateContact(recordId: number, contact: ContactTarget, label: string) {
  try {
    const auger = getAuger(client)
    await auger.record(recordId).contacts.add("contacts", contact)
  } catch (err) {
    console.error(`Failed to save ${label}:`, err)
    toast.warning(`Organization created, but failed to save ${label}.`)
  }
}

async function submit() {
  if (!canSubmit.value || submitting.value) return

  submitting.value = true
  try {
    const orgType = getType("organization")

    // Fold every enriched data field we have into record.data so the new
    // record is fully populated on day one. Fields the user edited take
    // precedence via the form watcher above; fields Harmonic supplied but we
    // don't expose as inputs are pulled from extra. The logo is an image
    // field, uploaded once the record exists.
    const data: Record<string, unknown> = {}
    if (extra.value.headcount != null) data.headcount = extra.value.headcount
    if (extra.value.stage) data.stage = extra.value.stage

    const payload: CreateRecordInput = {
      type: "organization",
      name: form.value.name.trim(),
      schema_id: orgType?.schema_id,
      data,
    }
    if (form.value.description.trim()) {
      payload.description = form.value.description.trim()
    }

    const record = await getAuger(client).records.create(payload)

    if (form.value.logo) {
      try {
        const asset = await uploadUrl(form.value.logo, { record, origin: "harmonic_enrichment" })
        await attachAndUpdate(asset, record)
      } catch (err) {
        console.error("Failed to save logo:", err)
      }
    }

    // Best-effort contacts — a failure on any single one shouldn't roll back
    // the record creation. Order matters for isPrimary: website first.
    const linkContacts = [
      form.value.website.trim() && {
        contactType: "url",
        value: form.value.website.trim(),
        label: "website",
        isPrimary: true,
      },
      form.value.linkedin.trim() && {
        contactType: "url",
        value: form.value.linkedin.trim(),
        label: "linkedin",
      },
      extra.value.twitter && {
        contactType: "url",
        value: extra.value.twitter,
        label: "twitter",
      },
      extra.value.facebook && {
        contactType: "url",
        value: extra.value.facebook,
        label: "facebook",
      },
    ].filter((c): c is ContactTarget & { label: string } => !!c)

    for (const contact of linkContacts) {
      await tryCreateContact(record.id, contact, contact.label)
    }

    if (extra.value.location) {
      await tryCreateContact(
        record.id,
        { contactType: "location", value: extra.value.location },
        "location"
      )
    }

    toast.success(`Created organization: ${record.name}`)
    orgEnrich.created(record)
  } catch (err) {
    const error = errorMessage(err)
    toast.error(`Failed to create organization: ${error}`)
  } finally {
    submitting.value = false
  }
}
</script>

<style>
.org-enrich-modal {
  .field {
    margin-bottom: 0.75rem;
  }

  .label {
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .modal-header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .org-logo {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    object-fit: cover;
    border: 1px solid var(--auger-border-light);
    background: var(--auger-bg);
  }

  .org-logo-initials {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--auger-text-weak);
    font-weight: 600;
  }

  .enriched-badge {
    color: var(--auger-link-text);
  }

  .enriched-meta {
    margin: 0.75rem 0;
    padding: 0.5rem 0.75rem;
    background: var(--auger-bg-muted);
    border-radius: var(--auger-radius);
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    .meta-row {
      display: flex;
      gap: 0.5rem;
    }
  }

  .enrich-warning {
    margin-top: 0.5rem;
  }

  .dup-warning {
    margin-top: 0.75rem;
    padding: 0.6rem 0.75rem;
    background: var(--auger-bg-warning, var(--auger-bg-muted));
    border: 1px solid var(--auger-border-warning, var(--auger-border));
    border-radius: var(--auger-radius);
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .dup-warning-header {
    font-weight: 600;
  }

  .dup-match-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .dup-match-name {
    flex: 1;
  }

  .dup-ack {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.25rem;
    cursor: pointer;
  }
}
</style>
